import React, { useState, useEffect, useCallback } from "react";
import { SimpleModulePage } from "../modules/SimpleModulePage";
import { config } from "../config/environment";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface PersonalSettingsPageProps {
  onBack: () => void;
}

interface HealthAdviceSettings {
  health_advice_use_height_weight: number;
  health_advice_use_medication_info: number;
  health_advice_use_blood_pressure: number;
  health_advice_use_blood_glucose: number;
  health_advice_use_diet_info: number;
  health_advice_use_steps_activity: number;
  health_advice_use_sleep_info: number;
}

/**
 * 허비서 설정 페이지
 * 건강조언에 참고할 항목을 선택하는 페이지
 */
export const PersonalSettingsPage: React.FC<PersonalSettingsPageProps> = ({ onBack }) => {
  const [token, setToken] = useState<string | null>(null);

  // 데이터베이스 저장된 설정
  const [settings, setSettings] = useState<HealthAdviceSettings>({
    health_advice_use_height_weight: 0,
    health_advice_use_medication_info: 0,
    health_advice_use_blood_pressure: 0,
    health_advice_use_blood_glucose: 0,
    health_advice_use_diet_info: 0,
    health_advice_use_steps_activity: 0,
    health_advice_use_sleep_info: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // 토큰 로드 (MyInfoPage와 동일한 패턴)
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    setToken(storedToken);
  }, []);

  // 설정 항목 정의
  const settingItems = [
    {
      key: 'health_advice_use_height_weight' as const,
      label: '신장/체중',
      description: '신체 사이즈 정보',
      icon: '📏',
    },
    {
      key: 'health_advice_use_medication_info' as const,
      label: '복약정보',
      description: '복용 중인 약물 정보',
      icon: '💊',
    },
    {
      key: 'health_advice_use_blood_pressure' as const,
      label: '혈압정보',
      description: '혈압 측정 데이터',
      icon: '🩸',
    },
    {
      key: 'health_advice_use_blood_glucose' as const,
      label: '혈당정보',
      description: '혈당 측정 데이터',
      icon: '🩺',
    },
    {
      key: 'health_advice_use_diet_info' as const,
      label: '식단정보',
      description: '식사 패턴 및 영양 정보',
      icon: '🥗',
    },
    {
      key: 'health_advice_use_steps_activity' as const,
      label: '걸음정보',
      description: '일일 활동량 및 걸음수',
      icon: '👟',
    },
    {
      key: 'health_advice_use_sleep_info' as const,
      label: '수면정보',
      description: '수면 시간 및 질',
      icon: '😴',
    },
  ] as const;

  /**
   * 백엔드에서 사용자의 현재 설정 조회
   */
  const fetchSettings = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${config.apiUrl}/user/health-advice-settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        setSettings({
          health_advice_use_height_weight: data.data.health_advice_use_height_weight || 0,
          health_advice_use_medication_info: data.data.health_advice_use_medication_info || 0,
          health_advice_use_blood_pressure: data.data.health_advice_use_blood_pressure || 0,
          health_advice_use_blood_glucose: data.data.health_advice_use_blood_glucose || 0,
          health_advice_use_diet_info: data.data.health_advice_use_diet_info || 0,
          health_advice_use_steps_activity: data.data.health_advice_use_steps_activity || 0,
          health_advice_use_sleep_info: data.data.health_advice_use_sleep_info || 0,
        });

        if (data.data.health_advice_settings_updated_at) {
          const date = new Date(data.data.health_advice_settings_updated_at);
          setLastSavedAt(date.toLocaleString('ko-KR'));
        }
      } else {
        throw new Error(data.message || '설정 조회 실패');
      }
    } catch (err) {
      console.error('[PersonalSettingsPage] 설정 조회 오류:', err);
      setError(err instanceof Error ? err.message : '설정을 조회할 수 없습니다');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // 토큰 로드 후 설정 조회
  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token, fetchSettings]);

  /**
   * 현재 설정을 백엔드에 저장
   * MyInfoPage와 동일한 패턴 (config.apiUrl 사용)
   */
  const saveSettings = async (updatedSettings: HealthAdviceSettings) => {
    if (!token) return;

    try {
      setIsSaving(true);
      setError(null);

      const response = await fetch(`${config.apiUrl}/user/health-advice-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedSettings),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log(`✅ 설정 저장 성공:`, data.data);
        if (data.data?.updatedAt) {
          const date = new Date(data.data.updatedAt);
          setLastSavedAt(date.toLocaleString('ko-KR'));
        }
      } else {
        throw new Error(data.message || '설정 저장 실패');
      }
    } catch (err) {
      console.error('[PersonalSettingsPage] 설정 저장 오류:', err);
      setError(err instanceof Error ? err.message : '설정을 저장할 수 없습니다');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * 토글 변경 핸들러
   */
  const handleToggle = async (fieldName: keyof HealthAdviceSettings) => {
    const updatedSettings = {
      ...settings,
      [fieldName]: settings[fieldName] === 0 ? 1 : 0,
    };

    setSettings(updatedSettings);
    await saveSettings(updatedSettings);
  };

  /**
   * 선택된 항목 개수 계산
   */
  const getEnabledCount = (): number => {
    return Object.values(settings).filter((v) => v === 1).length;
  };

  const enabledCount = getEnabledCount();
  const totalCount = settingItems.length;

  if (isLoading) {
    return (
      <SimpleModulePage title="허비서 설정" subtitle="(개인화 설정)" icon="⚙️" onBack={onBack}>
        <SectionCard className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-[32px] mb-3 animate-pulse">💫</div>
            <p className="text-slate-500 text-[14px]">설정을 불러오는 중...</p>
          </div>
        </SectionCard>
      </SimpleModulePage>
    );
  }

  return (
    <SimpleModulePage title="허비서 설정" subtitle="(개인화 설정)" icon="⚙️" onBack={onBack}>
      {/* 에러 메시지 */}
      {error && (
        <SectionCard className="bg-red-50 border-red-200">
          <div className="flex gap-3">
            <div className="text-[20px] flex-shrink-0">⚠️</div>
            <div>
              <p className="text-[13px] font-semibold text-red-700 mb-1">오류 발생</p>
              <p className="text-[12px] text-red-600 mb-2">{error}</p>
              <button
                onClick={fetchSettings}
                className="text-[12px] text-red-700 hover:text-red-800 underline"
              >
                다시 불러오기
              </button>
            </div>
          </div>
        </SectionCard>
      )}

      {/* 상단 안내 카드 */}
      <SectionCard className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex gap-3">
          <div className="text-[24px] flex-shrink-0">📋</div>
          <div>
            <h3 className="text-[14px] font-semibold text-slate-900 mb-1">허비서 설정이란?</h3>
            <p className="text-[12px] text-slate-600 leading-relaxed">
              건강조언을 더욱 정확하고 개인화된 내용으로 제공하기 위해, 
              참고하고자 하는 건강 정보를 선택해주세요. 
              선택된 정보는 AI 기반의 맞춤형 건강조언 생성에만 사용됩니다.
            </p>
          </div>
        </div>
      </SectionCard>

      {/* 설정 안내 */}
      <SectionCard>
        <p className="text-[12px] text-slate-600 font-medium">
          ✓ 건강조언에 참고하고자 하는 항목을 선택해주세요
        </p>
      </SectionCard>

      {/* 설정 항목 */}
      <SectionCard>
        <div className="space-y-0">
          {settingItems.map((item, index) => (
            <div
              key={item.key}
              className={`py-4 px-0 flex items-center justify-between ${
                index !== settingItems.length - 1 ? 'border-b border-slate-100' : ''
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <span className="text-[20px]">{item.icon}</span>
                <div>
                  <div className="text-[14px] font-semibold text-slate-900">{item.label}</div>
                  <div className="text-[12px] text-slate-500">{item.description}</div>
                </div>
              </div>

              {/* 토글 스위치 */}
              <button
                onClick={() => handleToggle(item.key)}
                disabled={isSaving}
                className={`flex-shrink-0 w-12 h-6 rounded-full transition-all duration-300 flex items-center ${
                  settings[item.key] === 1
                    ? 'bg-green-500'
                    : 'bg-slate-300'
                } ${isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
              >
                <span
                  className={`w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${
                    settings[item.key] === 1
                      ? 'translate-x-6'
                      : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* 설정 요약 */}
      <SectionCard className="bg-slate-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[12px] text-slate-600 mb-1">현재 활성화된 항목</p>
            <p className="text-[16px] font-semibold text-slate-900">
              {enabledCount} / {totalCount}개 선택됨
            </p>
            {lastSavedAt && (
              <p className="text-[11px] text-slate-500 mt-1">
                저장됨: {lastSavedAt}
              </p>
            )}
          </div>
          <div className="text-[32px]">{isSaving ? '⏳' : '✓'}</div>
        </div>
      </SectionCard>

      {/* 하단 안내 메시지 */}
      <SectionCard>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          💡 설정을 변경하면 자동으로 저장됩니다. 
          매일 새벽 2시에 생성되는 건강조언이 
          당신이 선택한 정보를 바탕으로 맞춤형으로 제공됩니다.
        </p>
      </SectionCard>

      <div className="h-6" />
    </SimpleModulePage>
  );
};
